import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Modal,
    List,
    ListItem,
    ListItemText,
    Button,
    Tabs,
    Tab,
    TextField,
    CircularProgress,
} from "@mui/material";
import { emptyProperty, FullProperty } from "../models/fullProperty";
import { CartItem, CartStatus, CartType } from "../models/CartType";
import { useParams } from "react-router-dom";
import { StringsService } from "../services/strings/strings";
import { restaurantService } from "../services/firebase/restaurantManagementService";
import { CartService } from "../services/functions/cartService";
import { OrderHistoryService } from "../services/order/orderHistoryService";

// Define the order type
interface Order {
    id: string;
    items: { name: string; quantity: number; dietryRequirements: string[] }[];
}

type OrderNextStage = "BEING_PREPARED" | "READY_FOR_COLLECTION" | "COLLECTED"

const OrderManagementPage: React.FC = () => {
    const { id } = useParams();
    const [carts, setCarts] = useState<{ [cartId: string]: CartType }>({});
    const [cartItemNames, setCartItemNames] = useState<{ [cartId: string]: string }>({});
    const [activeTab, setActiveTab] = useState(0);
    const [restaurantName, setRestaurantName] = useState<string>("");
    const [shortSummary, setShortSummary] = useState<string>("");
    const [thumbnail, setThumbnail] = useState<string>("");
    const [listing, SetListing] = useState<FullProperty>(emptyProperty);
    const [isLoading, setIsLoading] = useState(false)
    const [openModal, setOpenModal] = useState(false);
    const [confirmationNumber, setConfirmationNumber] = useState("");
    const [orderDetails, setOrderDetails] = useState<{ orderId: string, orderSaleItems: string } | null>(null);

    useEffect(() => {
        const fetchStrings = async () => {
            const name = await StringsService.getStringById(`${id}-restaurant-name`);
            const summary = await StringsService.getStringById(`${id}-short-restaurant-summary`);
            const image = await StringsService.getStringById(`${id}-thumbnail`);
            setRestaurantName(name);
            setShortSummary(summary);
            setThumbnail(image);
            const listing = await restaurantService.fetchRestaurantById(id ?? "")
            SetListing(listing)
            if (listing) {
                const allOrders = [
                    ...(listing.incomingOrders ?? []),
                    ...(listing.ordersBeingPrepared ?? []),
                    ...(listing.ordersReadyForCollection ?? [])
                ]
                let newCarts: { [cartId: string]: CartType } = {}
                let newCartItemNames: { [cartId: string]: string } = {}
                const promises = allOrders.map(async (orderId: string) => {
                    const cart = await OrderHistoryService.getOrderById(orderId);

                    if (cart) {
                        newCarts[orderId] = cart;
                        let saleItems: string[] = []
                        const saleItemPromises = cart.items.map(async (elm: CartItem) => {
                            const itemName = elm.menuCartItem.name;
                            saleItems.push(`${itemName} (x ${elm.quantity})`);
                        })
                        await Promise.all(saleItemPromises)
                        newCartItemNames[orderId] = saleItems.join(", ")
                    }
                })
                await Promise.all(promises)
                setCarts(newCarts)
                setCartItemNames(newCartItemNames)
            }
        };

        fetchStrings();
    }, [id]);

    const handleStatusChange = async (orderId: string, newStatus: string) => {
        const updatedOrders: FullProperty = { ...listing };

        // Remove the order from the current status
        if (updatedOrders.incomingOrders.includes(orderId)) {
            updatedOrders.incomingOrders = updatedOrders.incomingOrders.filter(
                (id) => id !== orderId
            );
        } else if (updatedOrders.ordersBeingPrepared.includes(orderId)) {
            updatedOrders.ordersBeingPrepared = updatedOrders.ordersBeingPrepared.filter(
                (id) => id !== orderId
            );
        } else if (updatedOrders.ordersReadyForCollection.includes(orderId)) {
            updatedOrders.ordersReadyForCollection = updatedOrders.ordersReadyForCollection.filter(
                (id) => id !== orderId
            );
        }

        // Add the order to the new status
        if (newStatus === "BEING_PREPARED") {
            if (!updatedOrders.ordersBeingPrepared) {
                updatedOrders.ordersBeingPrepared = []
            }
            updatedOrders.ordersBeingPrepared.push(orderId);
        } else if (newStatus === "READY_FOR_COLLECTION") {
            if (!updatedOrders.ordersReadyForCollection) {
                updatedOrders.ordersReadyForCollection = []
            }
            updatedOrders.ordersReadyForCollection.push(orderId);
        }

        // Save changes in the backend
        await restaurantService.editRestaurant(updatedOrders.id, updatedOrders);

        // Update state
        SetListing(updatedOrders);
    };

    const showButton = (nextStatus: OrderNextStage) => {
        if (nextStatus === "BEING_PREPARED" || nextStatus === "READY_FOR_COLLECTION") {
            return true
        }
        return false
    }

    const buttonText = (nextStatus: OrderNextStage) => {
        if (nextStatus === "BEING_PREPARED") {
            return "Being Prepared"
        }
        return "Ready for Collection"
    }

    // Handle Process Collection modal submission
    const handleProcessCollection = async () => {
        setIsLoading(true);
        const collectOrder = await CartService.collectOrder({
            orderNumberId: parseInt(confirmationNumber),
            restaurantId: id ?? ""
        });
        if (collectOrder.success && collectOrder.order) {
            setConfirmationNumber("");
            var orderId = collectOrder.order.id;
            var orderDescription = ``;
            var promises = collectOrder.order.items.map(async (orderItem) => {
                var saleItemName = orderItem.menuCartItem.name
                orderDescription += `${orderItem.quantity}x ${saleItemName}\n`
            })
            await Promise.all(promises)
            setOrderDetails({ orderId: orderId, orderSaleItems: orderDescription });
        } else {
            alert(collectOrder.error);
        }
        setIsLoading(false)
    };

    const renderOrders = (orderIds: string[], nextStatus: OrderNextStage) => {
        return (
            <List>
                {orderIds.map((orderId) => {
                    const cart = carts[orderId];
                    return (
                        <ListItem key={orderId}>
                            <Paper sx={{ padding: 2, width: "100%" }}>
                                <ListItemText
                                    primary={`Order #${orderId}`}
                                    secondary={`Items: ${cartItemNames[orderId]}`}
                                />
                                {
                                    showButton(nextStatus) &&
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px"}}
                                        onClick={() => handleStatusChange(orderId, nextStatus)}
                                    >
                                        Move to {buttonText(nextStatus)}
                                    </Button>
                                }
                            </Paper>
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    return (
        <Box sx={{ padding: 3, backgroundColor: "#f5f9e9", height: "100vh" }} className="body">
            <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
                {restaurantName}
            </Typography>
            <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                aria-label="Order management tabs"
                sx={{
                    "& .MuiTab-root": {
                        color: "#757575",
                        transition: "background-color 0.3s",
                    },
                    "& .Mui-selected": {
                        color: "#162C1F !important",
                        fontWeight: "bold",
                    },
                    "& .MuiTab-root:hover": {
                        backgroundColor: "#E6F0E8",
                    },
                    "& .MuiTabs-indicator": {
                        backgroundColor: "#162C1F",
                    },
                }}
            >
                <Tab label="Incoming Orders" />
                <Tab label="Being Prepared" />
                <Tab label="Ready for Collection" />

                <Button
                    variant="contained"
                    sx={{ margin: "0 8px", background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                    onClick={() => {
                        setOpenModal(true);
                    }}
                >
                    Process Collection
                </Button>
            </Tabs>

            <Box sx={{ marginTop: 2 }}>
                {activeTab === 0 &&
                    renderOrders(listing.incomingOrders || [], "BEING_PREPARED")}
                {activeTab === 1 &&
                    renderOrders(listing.ordersBeingPrepared || [], "READY_FOR_COLLECTION")}
                {activeTab === 2 &&
                    renderOrders(listing.ordersReadyForCollection || [], "COLLECTED")}
            </Box>



            {/* Process Collection Modal */}


            <Modal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setConfirmationNumber("");
                    setOrderDetails(null);
                }}
            >

                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Process Order Collection
                    </Typography>
                    <TextField
                        fullWidth
                        label="Confirmation Number"
                        value={confirmationNumber}
                        onChange={(e) => !isLoading ? setConfirmationNumber(e.target.value) : null}
                        margin="normal"
                        disabled={!!orderDetails}
                    />

                    {
                        isLoading ?
                            <CircularProgress size={24} sx={{ color: "black" }} />
                            :
                            <Button
                                variant="contained"
                                onClick={handleProcessCollection}
                                fullWidth

                                sx={{ mt: 1,margin: "0 8px", background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                            >
                                Confirm
                            </Button>
                    }
                    {orderDetails && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body1">
                                Order #{orderDetails.orderId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Items: {orderDetails.orderSaleItems}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default OrderManagementPage;